import styles from './ccf-interaction-search-cases-tab.module.scss';
import { useEffect, useState, useRef } from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Stack from '@mui/material/Stack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Chip from '@mui/material/Chip';
import { emphasize, styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import makeStyles from '@mui/styles/makeStyles';

import {
  FormControl,
  MenuItem,
  Select,
  Theme,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CcfAlert,
  CcfBox,
  CcfButton,
  CcfSnackBar,
  CcfTextField,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { useMsal } from '@azure/msal-react';

/* eslint-disable-next-line */
export interface CcfInteractionSearchCasesCheckboxesProps {
  selectedRowData: any;
  setRowData: any;
  rowData: any;
  setSelectedRowData: any;
  setSelectedIds: any;
  selectedIds: any;
}

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === 'light'
      ? theme.palette.grey[100]
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    // color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    '&:hover, &:focus': {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    '&:active': {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
}) as typeof Chip; // TypeScript only: need a type cast here because https://github.com/Microsoft/TypeScript/issues/26591

const useStyles = makeStyles({
  suggestionField: {
    display: 'flex',
    justifyContent: 'center',
    minHeight: '48px',
    background: '#F7F7F7',
    whiteSpace: 'normal',
  },
  suggestionInputPropsStyle: {
    '&.MuiInputBase-root': {
      color: '#3F5C69',
      fontFamily: 'Open Sans',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '125%',
    },
  },
});

/**
 * CcfInteractionSearch - used to display Notes in contact card
 * @param props -? - CcfInteractionSearchCasesCheckboxesProps
 * @example - <CcfInteractionSearchCasesCheckboxes />
 */
export function CcfInteractionSearchCasesCheckboxes({
  selectedRowData,
  setSelectedRowData,
  setSelectedIds,
  setRowData,
  rowData,
  selectedIds,
}: CcfInteractionSearchCasesCheckboxesProps) {
  const { accounts } = useMsal();
  const classes = useStyles();
  const [translate] = useTranslator();
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const loginCredentialsArray = loginCredentialsFromSSO?.split(' ');

  const assignName = `${
    loginCredentialsArray[0].charAt(0).toUpperCase() +
    loginCredentialsArray[0].slice(1).toLowerCase()
  } ${
    loginCredentialsArray[1].charAt(0).toUpperCase() +
    loginCredentialsArray[1].slice(1).toLowerCase()
  }`;

  const breadcrumbData = [
    {
      options: [assignName],
      selectedOption: assignName,
      selectedType: 'assignToMe',
      isSelected: false,
      isActive: true,
      id: 0,
    },
    {
      options: [
        'Janus Doesne',
        'John Smith',
        'Kalby Awadkansee',
        'John Smithson',
        'Janwdye Shealty',
        'Janet Dolgera',
      ],
      selectedOption: 'Janus Doesne',
      selectedType: 'assignToOthers',
      isSelected: false,
      isActive: true,
      id: 1,
    },
    {
      options: ['new', 'closed'],
      selectedOption: 'new',
      selectedType: 'changeStatus',
      isSelected: false,
      isActive: true,
      id: 2,
    },
    {
      selectedText: '',
      selectedType: 'sendMessage',
      isSelected: false,
      isActive: true,
      id: 3,
    },
    {
      formatoptions: ['CSV'],
      selectedOption: '',
      selectedType: 'export',
      isSelected: false,
      isActive: false,
      id: 4,
    },
  ];
  const [selectedComponent, setSelectedComponent]: any =
    useState(breadcrumbData);

  const [textAreaValue, setTextAreaValue] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [flag, setFlag] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const theme: Theme = useTheme();
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'), { noSsr: true });

  useEffect(() => {
    if (flag) {
      setTimeout(() => {
        setSelectedRowData([]);
      }, 2500);
    }
  }, [flag]);

  /**
   * handleClick handle item click event
   * @example <handleClick />
   */
  function handleClick(event: any) {
    event.preventDefault();
    const setData = breadcrumbData.map((x: any, i: any) =>
      translate(x.selectedType) === event.target.textContent
        ? { ...x, isSelected: !selectedComponent[i].isSelected }
        : { ...x }
    );
    setSelectedComponent(setData);
  }

  /**
   * handleClick handle item click event
   * @example <handleClick />
   */
  // Generic Assign Component for both "Assign to Me" and "Assign to Others"
  interface AssignComponentProps {
    idx: number;
    labelTranslationKey: string;
    buttonTranslationKey: string;
    menuItemIdPrefix: string;
    menuOptions: string[];
  }

  const AssignComponentGeneric = ({
    idx,
    labelTranslationKey,
    buttonTranslationKey,
    menuItemIdPrefix,
    menuOptions,
  }: AssignComponentProps) => {
    return (
      <CcfBox
        style={{
          margin: '0px 17px 13px',
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: 'column',
        }}
        id={`agent_interactionSearch_assign_mainContainer_${idx}`}
      >
        <CcfTypography
          className={styles.selectLabel}
          translationKey={'assignTo'}
        />
        <CcfBox
          style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '5px',
            gap: '10px',
          }}
        >
          <FormControl
            variant="outlined"
            size={'small'}
            style={{
              width: !isSmView ? '274px' : '210px',
            }}
          >
            <Select
              labelId="demo-simple-select-label"
              variant="outlined"
              sx={{
                '&& fieldset': {
                  border: '1px solid #c4c4c4',
                },
              }}
              id={`agent_interactionSearch_assignTo_dropdown_${idx}`}
              className={styles.selectBox}
              IconComponent={() => (
                <ExpandMoreIcon
                  style={{ color: '#616161', marginRight: '5px' }}
                />
              )}
              value={selectedComponent[idx].selectedOption}
              onChange={(event: any) => {
                setSelectedComponent((prevData: any) =>
                  prevData.map((item: any, i: any) =>
                    i === idx
                      ? { ...item, selectedOption: event.target.value }
                      : item
                  )
                );
              }}
            >
              {menuOptions?.map((option: string, index: number) => (
                <MenuItem
                  value={option}
                  key={index}
                  className={styles.labelText}
                  id={`${menuItemIdPrefix}_${option}`}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <CcfButton
            className={styles.btn}
            primary
            id={`agent_interactionSearch_assign_button_${idx}`}
            onClick={() => {
              const updatedData = JSON.parse(JSON.stringify(rowData));
              selectedRowData.forEach((updateObj: any) => {
                const objIndex = updatedData.findIndex(
                  (obj: any) => obj.id === updateObj.id
                );
                if (objIndex !== -1) {
                  updatedData[objIndex] = {
                    ...updatedData[objIndex],
                    ...updateObj,
                    inboxAssigneeUser: selectedComponent[idx].selectedOption,
                    ownerAssigneeUser: selectedComponent[idx].selectedOption,
                  };
                }
              });
              setRowData(updatedData);
              setToastMessage(
                `${translate('statusChangeToastMsg')} ${
                  selectedComponent[idx].selectedOption
                }`
              );
              setShowToast(true);
              setFlag(true);

              setSelectedIds([]);
              setSelectedComponent((prevData: any) =>
                prevData.map((item: any, i: any) =>
                  i === idx ? { ...item, selectedOption: '' } : item
                )
              );
            }}
          >
            <CcfTypography
              className={styles.labelText}
              translationKey={buttonTranslationKey}
            />
          </CcfButton>{' '}
        </CcfBox>
      </CcfBox>
    );
  };

  // Wrapper components for compatibility with previous usage
  const AssignComponent = () => (
    <AssignComponentGeneric
      idx={0}
      labelTranslationKey="assignTo"
      buttonTranslationKey="assignToMe"
      menuItemIdPrefix="agent_interactionSearch_AssignTo_filter"
      menuOptions={breadcrumbData[0]?.options || []}
    />
  );

  const AssignOthersComponent = () => (
    <AssignComponentGeneric
      idx={1}
      labelTranslationKey="assignTo"
      buttonTranslationKey="assign"
      menuItemIdPrefix="agent_interactionSearch_AssignOthers_filter"
      menuOptions={breadcrumbData[1]?.options || []}
    />
  );

  /**
   * handleClick handle item click event
   * @example <handleClick />
   */
  const EditStatusComponent = () => {
    return (
      <CcfBox
        style={{
          margin: '0px 17px 13px',
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: 'column',
          width: '375px',
        }}
        id={`agent_interactionSearch_edit_breadcrumb_container`}
      >
        <CcfTypography
          className={styles.selectLabel}
          translationKey={'changeStatusTo'}
        />
        <CcfBox
          style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '5px',
            gap: '10px',
          }}
        >
          <FormControl
            variant="outlined"
            size={'small'}
            style={{
              width: !isSmView ? '274px' : '210px',
            }}
          >
            <Select
              labelId="demo-simple-select-label"
              variant="outlined"
              id={`agent_interactionSearch_changeStatus_dropdown`}
              sx={{
                '&& fieldset': {
                  border: '1px solid #c4c4c4',
                },
              }}
              className={styles.selectBox}
              IconComponent={() => (
                <ExpandMoreIcon
                  style={{ color: '#616161', marginRight: '5px' }}
                />
              )}
              value={selectedComponent[2].selectedOption}
              onChange={(event: any) => {
                setSelectedComponent((prevData: any) =>
                  prevData.map((item: any, idx: any) =>
                    idx === 2
                      ? { ...item, selectedOption: event.target.value }
                      : item
                  )
                );
              }}
            >
              {breadcrumbData[2]?.options?.map(
                (option: string, index: number) => {
                  return (
                    <MenuItem
                      value={option}
                      key={index}
                      id={`agent_interactionSearch_AssignTo_filter_${option}`}
                    >
                      {translate(option)}
                    </MenuItem>
                  );
                }
              )}
            </Select>
          </FormControl>
          <CcfButton
            className={styles.btn}
            primary
            id={`agent_interactionSearch_assign_button`}
            onClick={() => {
              const updatedData = JSON.parse(JSON.stringify(rowData));
              selectedRowData.forEach((updateObj: any) => {
                const objIndex = updatedData.findIndex(
                  (obj: any) => obj.id === updateObj.id
                );
                if (objIndex !== -1) {
                  updatedData[objIndex] = {
                    ...updatedData[objIndex],
                    ...updateObj,
                    status: selectedComponent[2].selectedOption,
                  };
                }
              });
              setRowData(updatedData);
              setToastMessage(
                `${translate('selectedinteractionstatushasbeenchangeto')}  ${
                  selectedComponent[2].selectedOption
                }`
              );
              setShowToast(true);
              setFlag(true);

              setSelectedIds([]);
              setSelectedComponent((prevData: any) =>
                prevData.map((item: any, idx: any) =>
                  idx === 2 ? { ...item, selectedOption: '' } : item
                )
              );
            }}
          >
            <CcfTypography
              className={styles.btnText}
              translationKey={'change'}
            />
          </CcfButton>{' '}
        </CcfBox>
        <CcfTypography
          style={{ margin: '15px 4px 0px', width: '300px' }}
          className={styles.labelText}
        >
          {selectedRowData.length}{' '}
          {translate('selectedinteractionswillhavetheirstatus ')}
          {translate('changeStatusTo')} {selectedComponent[2].selectedOption}.
        </CcfTypography>
      </CcfBox>
    );
  };

  /**
   * handleInputChange handle item click event
   * @example <handleInputChange />
   */
  function handleInputChange(event: any) {
    // event.preventDefault();

    setTextAreaValue(event);
    setSelectedComponent((prevData: any) =>
      prevData.map((item: any, idx: any) =>
        idx === 2 ? { ...item, selectedText: event } : item
      )
    );
  }
  useEffect(() => {
    handleInputChange(textAreaRef?.current?.value);
  }, [textAreaRef]);
  /**
   * handleClick handle item click event
   * @example <handleClick />
   */
  const ReplyComponent = () => {
    return (
      <CcfBox
        className={styles.replyHeader}
        sx={{ width: !isSmView ? '440px' : '300px' }}
        id={`agent_interactionSearch_reply_breadcrumb_container`}
      >
        <CcfTypography
          className={styles.selectLabel}
          translationKey={'sendMessage'}
        />
        <CcfBox
          className={styles.replyTextArea}
          sx={{ width: !isSmView ? '441px' : '300px !important' }}
        >
          <CcfTextField
            fullWidth
            multiline={true}
            autoFocus
            rows={3}
            maxRows={3}
            id="agent_interactionSearch_cases_reply_textarea"
            className={styles.reply_textarea}
            // value={textAreaValue}
            // onChange={(event: any) => {
            //   handleInputChange(event);
            // }}
            InputProps={{
              className: classes.suggestionInputPropsStyle,
              disableUnderline: true,
            }}
            inputRef={textAreaRef}
            variant="standard"
            size="medium"
            focused={false}
          ></CcfTextField>
          <CcfBox>
            <img
              src={'/assets/textAreaIcons.png'}
              style={{ width: '130px', height: '35px' }}
              alt={'textAreaIcons'}
            />
          </CcfBox>
        </CcfBox>
        <CcfBox
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <CcfButton
            className={styles.sendBtn}
            primary
            id={`agent_interactionSearch_reply_send_button`}
            onClick={() => {
              setToastMessage(`${translate('customizedreplymessage')}`);
              setShowToast(true);
              setFlag(true);

              setSelectedIds([]);
              setTextAreaValue('');
              setSelectedComponent((prevData: any) =>
                prevData.map((item: any, idx: any) =>
                  idx === 2 ? { ...item, selectedText: '' } : item
                )
              );
            }}
          >
            <CcfTypography className={styles.btnText} translationKey={'send'} />
          </CcfButton>{' '}
        </CcfBox>
        <CcfBox style={{ marginTop: '10px' }}>
          <CcfTypography style={{ fontSize: '12px' }}>
            {translate('interactionStatusChangeNote1')}
            <span style={{ fontWeight: '600' }}>{translate('new')} </span>
            {translate('interactionStatusChangeNote2')}
            <span style={{ fontWeight: '600' }}>
              {translate('closed')}
            </span>{' '}
            {translate('interactions')}.
          </CcfTypography>
        </CcfBox>
      </CcfBox>
    );
  };

  /**
   * arrayToCSV handle item click event
   * @example <arrayToCSV />
   */
  function arrayToCSV(data: any) {
    const csvRows = [];

    // Get the headers (keys of the objects)
    const headers = Object.keys(data[0]);
    csvRows.push(headers.join(','));

    // Loop through the rows
    for (const row of data) {
      const values = headers.map((header) => {
        const escaped = ('' + row[header]).replace(/"/g, '\\"');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  }

  /**
   * arrayToCSV handle item click event
   * @example <arrayToCSV />
   */
  function downloadCSV(data: any, filename = 'InteractionData.csv') {
    const updatedData = data.map((x: any) => ({
      ...x,
      lastUpdated: new Date(x.lastUpdated),
    }));
    const csvString = arrayToCSV(updatedData);
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', filename);
    a.click();
    window.URL.revokeObjectURL(url);
  }
  /**
   * handleClick handle item click event
   * @example <handleClick />
   */
  const ExportComponent = () => {
    return (
      <CcfBox
        style={{
          margin: '0px 17px 13px',
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: 'column',
          width: !isSmView ? '410px' : '300px',
        }}
        id={`agent_interactionSearch_export_breadcrumb_container`}
      >
        <CcfBox
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginTop: '5px',
            gap: '10px',
            flexDirection: 'column',
          }}
          id={`agent_interactionSearch_export_container`}
        >
          <CcfTypography
            className={styles.selectLabel}
            translationKey={'exportFormat'}
          />
          <FormControl
            id={`agent_interactionSearch_export_dropdown`}
            variant="outlined"
            // className={classes.formControl}
            size={'small'}
            style={{
              width: !isSmView ? '410px' : '300px',
            }}
          >
            <Select
              labelId="demo-simple-select-label"
              variant="outlined"
              sx={{
                '&& fieldset': {
                  border: '1px solid #c4c4c4',
                },
              }}
              placeholder="Select file type"
              className={styles.selectBox}
              IconComponent={() => (
                <ExpandMoreIcon
                  style={{ color: '#616161', marginRight: '5px' }}
                />
              )}
              value={selectedComponent[1].selectedOption}
              onChange={(event: any) => {
                setSelectedComponent((prevData: any) =>
                  prevData.map((item: any, idx: any) =>
                    idx === 1
                      ? { ...item, selectedOption: event.target.value }
                      : item
                  )
                );
              }}
              // className={classes.input}
              // label={label}
            >
              {breadcrumbData[3]?.formatoptions?.map(
                (option: string, index: number) => {
                  return (
                    <MenuItem
                      value={option}
                      key={index}
                      id={`agent_interactionSearch_Export_filter_${option}`}
                    >
                      {option}
                    </MenuItem>
                  );
                }
              )}
            </Select>
          </FormControl>{' '}
        </CcfBox>
        <CcfBox
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginTop: '5px',
            gap: '10px',
            flexDirection: 'column',
          }}
          id={`agent_interactionSearch_location_container`}
        >
          <CcfTypography
            className={styles.selectLabel}
            translationKey={'location'}
          />

          <FormControl
            variant="outlined"
            // className={classes.formControl}
            size={'small'}
            style={{
              width: !isSmView ? '410px' : '300px',
            }}
          >
            <Select
              labelId="demo-simple-select-label"
              variant="outlined"
              sx={{
                '&& fieldset': {
                  border: '1px solid #c4c4c4',
                },
              }}
              id={`agent_interactionSearch_location_dropdown`}
              placeholder="Select"
              className={styles.selectBox}
              IconComponent={() => (
                <ExpandMoreIcon
                  style={{ color: '#616161', marginRight: '5px' }}
                />
              )}
              value={selectedComponent[1].selectedOption}
              onChange={(event: any) => {
                setSelectedComponent((prevData: any) =>
                  prevData.map((item: any, idx: any) =>
                    idx === 1
                      ? { ...item, selectedOption: event.target.value }
                      : item
                  )
                );
              }}
              // className={classes.input}
              // label={label}
            >
              {breadcrumbData[3]?.options?.map(
                (option: string, index: number) => {
                  return (
                    <MenuItem
                      value={option}
                      key={index}
                      id={`agent_interactionSearch_Export_filter_${option}`}
                    >
                      {option}
                    </MenuItem>
                  );
                }
              )}
            </Select>
          </FormControl>
        </CcfBox>
        <CcfBox
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            marginTop: '10px',
          }}
        >
          <CcfButton
            className={styles.sendBtn}
            primary
            id={`agent_interactionSearch_export_button`}
            onClick={() => {
              downloadCSV(selectedRowData);
              setFlag(true);
              setSelectedIds([]);
              setToastMessage(`${translate('importToasterMsg')}`);
              setShowToast(true);
              setSelectedComponent((prevData: any) =>
                prevData.map((item: any, idx: any) =>
                  idx === 1 ? { ...item, selectedOption: '' } : item
                )
              );
            }}
          >
            <CcfTypography
              className={styles.btnText}
              translationKey={'export'}
            />
          </CcfButton>{' '}
        </CcfBox>
      </CcfBox>
    );
  };

  return (
    <Stack spacing={2} id={`agent_interactionSearch_breadcrumbs_header`}>
      <Breadcrumbs
        style={{ padding: '15px 17px' }}
        aria-label="breadcrumb"
        aria-hidden="false"
        separator=""
      >
        <StyledBreadcrumb
          className={
            selectedComponent[0].selectedType === 'assignToMe' &&
            selectedComponent[0].isSelected
              ? styles.activeTab
              : undefined
          }
          id={`agent_interactionSearch_assign_breadcrumb`}
          style={{
            color: '#003D7A',
            fontWeight: '600',
            fontSize: '14px',
          }}
          label={translate('assignToMe')}
          aria-hidden="false"
          onClick={handleClick}
          icon={
            selectedComponent[0].selectedType === 'assignToMe' ? (
              <svg
                className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-vubbuv"
                focusable="false"
                aria-hidden="true"
                viewBox="-2 0 20 15"
                fill="#003D7A"
                fontSize="24px"
                width={24}
                height={24}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.58145 0.583253C5.58765 0.583254 5.59385 0.583255 5.60006 0.583255L8.41866 0.583253C8.56589 0.583236 8.70926 0.583219 8.83069 0.59314C8.96404 0.604034 9.12138 0.629741 9.27971 0.710414C9.49923 0.822267 9.67771 1.00074 9.78956 1.22027C9.87024 1.3786 9.89594 1.53594 9.90684 1.66929C9.90907 1.69667 9.91081 1.72516 9.91215 1.75457C10.1427 1.76201 10.3401 1.78105 10.5206 1.82943C11.3258 2.04518 11.9548 2.67413 12.1705 3.47934C12.2505 3.77781 12.2503 4.12236 12.2501 4.58634C12.2501 4.6127 12.2501 4.63944 12.2501 4.66659V10.0574C12.2501 10.5269 12.2501 10.9145 12.2243 11.2302C12.1975 11.558 12.14 11.8595 11.9957 12.1426C11.772 12.5816 11.4151 12.9386 10.976 13.1623C10.6929 13.3065 10.3915 13.364 10.0636 13.3908C9.74793 13.4166 9.36039 13.4166 8.89083 13.4166H5.10928C4.63972 13.4166 4.25219 13.4166 3.93649 13.3908C3.6086 13.364 3.30719 13.3065 3.02408 13.1623C2.58503 12.9386 2.22808 12.5816 2.00437 12.1426C1.86012 11.8595 1.80263 11.558 1.77584 11.2302C1.75004 10.9145 1.75005 10.5269 1.75006 10.0573L1.75006 4.66659C1.75006 4.63944 1.75004 4.61269 1.75003 4.58633C1.74978 4.12236 1.74959 3.77781 1.82956 3.47934C2.04532 2.67413 2.67426 2.04518 3.47948 1.82943C3.66003 1.78105 3.85745 1.76201 4.08797 1.75456C4.08931 1.72516 4.09104 1.69667 4.09327 1.66929C4.10417 1.53594 4.12988 1.3786 4.21055 1.22027C4.3224 1.00074 4.50088 0.822266 4.7204 0.710414C4.87873 0.629741 5.03608 0.604034 5.16942 0.59314C5.29085 0.583219 5.43423 0.583236 5.58145 0.583253ZM4.08845 2.92216C3.93498 2.92747 3.8515 2.93757 3.78143 2.95634C3.37883 3.06422 3.06435 3.37869 2.95648 3.7813C2.92173 3.91096 2.91672 4.08655 2.91672 4.66659V10.0333C2.91672 10.5329 2.91718 10.8726 2.93863 11.1351C2.95952 11.3909 2.9974 11.5217 3.04388 11.6129C3.15573 11.8324 3.33421 12.0109 3.55373 12.1228C3.64497 12.1692 3.77575 12.2071 4.0315 12.228C4.29405 12.2495 4.63372 12.2499 5.13339 12.2499H8.86672C9.36639 12.2499 9.70607 12.2495 9.96862 12.228C10.2244 12.2071 10.3551 12.1692 10.4464 12.1228C10.6659 12.0109 10.8444 11.8324 10.9562 11.6129C11.0027 11.5217 11.0406 11.3909 11.0615 11.1351C11.0829 10.8726 11.0834 10.5329 11.0834 10.0333V4.66659C11.0834 4.08655 11.0784 3.91096 11.0436 3.7813C10.9358 3.37869 10.6213 3.06422 10.2187 2.95634C10.1486 2.93757 10.0651 2.92747 9.91166 2.92216C9.9104 2.94796 9.90881 2.97303 9.90684 2.99722C9.89594 3.13057 9.87024 3.28791 9.78956 3.44624C9.67771 3.66577 9.49923 3.84424 9.27971 3.9561C9.12138 4.03677 8.96404 4.06248 8.83069 4.07337C8.70926 4.08329 8.56589 4.08327 8.41866 4.08326L5.60006 4.08325C5.59385 4.08325 5.58765 4.08326 5.58145 4.08326C5.43423 4.08327 5.29085 4.08329 5.16942 4.07337C5.03608 4.06248 4.87873 4.03677 4.7204 3.9561C4.50088 3.84424 4.3224 3.66577 4.21055 3.44624C4.12988 3.28791 4.10417 3.13057 4.09327 2.99722C4.0913 2.97303 4.08972 2.94796 4.08845 2.92216ZM8.75006 2.09992C8.75006 1.92695 8.7496 1.8323 8.74405 1.76429C8.74382 1.76158 8.7436 1.75902 8.74338 1.7566C8.74096 1.75638 8.73839 1.75615 8.73569 1.75593C8.66768 1.75038 8.57303 1.74992 8.40006 1.74992H5.60006C5.42708 1.74992 5.33243 1.75038 5.26443 1.75593C5.26172 1.75615 5.25916 1.75638 5.25673 1.7566C5.25651 1.75902 5.25629 1.76158 5.25607 1.76429C5.25051 1.8323 5.25006 1.92695 5.25006 2.09992V2.56659C5.25006 2.73956 5.25051 2.83421 5.25607 2.90222C5.25629 2.90493 5.25651 2.90749 5.25673 2.90991C5.25916 2.91013 5.26172 2.91036 5.26443 2.91058C5.33243 2.91613 5.42708 2.91659 5.60006 2.91659H8.40006C8.57303 2.91659 8.66768 2.91613 8.73569 2.91058C8.73839 2.91036 8.74096 2.91013 8.74338 2.90991C8.7436 2.90749 8.74382 2.90493 8.74405 2.90222C8.7496 2.83421 8.75006 2.73956 8.75006 2.56659V2.09992ZM9.4542 6.87911C9.68201 7.10691 9.68201 7.47626 9.4542 7.70407L6.8292 10.3291C6.6014 10.5569 6.23205 10.5569 6.00424 10.3291L4.83758 9.1624C4.60977 8.93459 4.60977 8.56525 4.83758 8.33744C5.06538 8.10964 5.43473 8.10964 5.66253 8.33744L6.41672 9.09163L8.62924 6.87911C8.85705 6.6513 9.2264 6.6513 9.4542 6.87911Z"
                ></path>
              </svg>
            ) : undefined
          }
        />
        <StyledBreadcrumb
          className={
            selectedComponent[1].selectedType === 'assignToOthers' &&
            selectedComponent[1].isSelected
              ? styles.activeTab
              : undefined
          }
          id={`agent_interactionSearch_assignOthers_breadcrumb`}
          style={{
            color: '#003D7A',
            fontWeight: '600',
            fontSize: '14px',
          }}
          label={translate('assignToOthers')}
          aria-hidden="false"
          onClick={handleClick}
          icon={
            selectedComponent[1].selectedType === 'assignToOthers' &&
            selectedComponent[1].isSelected ? (
              <ExpandMoreIcon />
            ) : (
              <NavigateNextIcon fontSize="small" />
            )
          }
        />
        <StyledBreadcrumb
          className={
            selectedComponent[2].selectedType === 'changeStatus' &&
            selectedComponent[2].isSelected
              ? styles.activeTab
              : undefined
          }
          id={`agent_interactionSearch_editStatus_breadcrumb`}
          aria-hidden="false"
          style={{ color: '#003D7A', fontWeight: '600', fontSize: '14px' }}
          label={translate('changeStatus')}
          onClick={handleClick}
          icon={
            selectedComponent[2].selectedType === 'changeStatus' &&
            selectedComponent[2].isSelected ? (
              <ExpandMoreIcon />
            ) : (
              <NavigateNextIcon fontSize="small" />
            )
          }
        />
        <StyledBreadcrumb
          className={
            selectedComponent[3].selectedType === 'sendMessage' &&
            selectedComponent[3].isSelected
              ? styles.activeTab
              : undefined
          }
          id={`agent_interactionSearch_reply_breadcrumb`}
          aria-hidden="false"
          label={translate('sendMessage')}
          onClick={handleClick}
          style={{ color: '#003D7A', fontWeight: '600', fontSize: '14px' }}
          icon={
            selectedComponent[3].selectedType === 'sendMessage' &&
            selectedComponent[3].isSelected ? (
              <ExpandMoreIcon />
            ) : (
              <NavigateNextIcon fontSize="small" />
            )
          }
        />
        {/* <StyledBreadcrumb
          aria-hidden="false"
          id={`agent_interactionSearch_export_breadcrumb`}
          className={
            selectedComponent[3].selectedType === 'export' &&
            selectedComponent[3].isSelected
              ? styles.activeTab
              : undefined
          }
          component="a"
          label={translate('export')}
          onClick={handleClick}
          style={{ color: '#003D7A', fontWeight: '600', fontSize: '14px' }}
          icon={
            selectedComponent[3].selectedType === 'export' &&
            selectedComponent[3].isSelected ? (
              <ExpandMoreIcon />
            ) : (
              <NavigateNextIcon fontSize="small" />
            )
          }
        /> */}
      </Breadcrumbs>
      <CcfBox>
        <CcfSnackBar
          open={showToast}
          autoHideDuration={3000}
          onClose={() => {
            setShowToast(false);
            setToastMessage('');
          }}
          anchorOrigin={{
            vertical: isSmView ? 'top' : 'bottom',
            horizontal: 'right',
          }}
        >
          <div>
            <CcfAlert
              closeAlert={() => {
                setShowToast(false);
                setToastMessage('');
              }}
              severity="success"
            >
              {toastMessage}
            </CcfAlert>
          </div>
        </CcfSnackBar>
      </CcfBox>
      {selectedComponent[0].selectedType === 'assignToMe' &&
        selectedComponent[0].isSelected && <AssignComponent />}
      {selectedComponent[1].selectedType === 'assignToOthers' &&
        selectedComponent[1].isSelected && <AssignOthersComponent />}
      {selectedComponent[2].selectedType === 'changeStatus' &&
        selectedComponent[2].isSelected && <EditStatusComponent />}
      {selectedComponent[3].selectedType === 'sendMessage' &&
        selectedComponent[3].isSelected && <ReplyComponent />}
      {/* {selectedComponent[3].selectedType === 'export' &&
        selectedComponent[3].isSelected && <ExportComponent />} */}
    </Stack>
  );
}

export default CcfInteractionSearchCasesCheckboxes;
